import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import type { PixelCrop } from './cropImage';

interface Props {
  imageUrl: string;
  onCancel: () => void;
  onConfirm: (crop: PixelCrop) => void;
}

export function ImageCropModal({ imageUrl, onCancel, onConfirm }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState<PixelCrop | null>(null);

  const onCropComplete = useCallback((_area: unknown, areaPixels: PixelCrop) => {
    setPixels(areaPixels);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return (
    <div
      className="image-crop-overlay"
      role="dialog"
      aria-label="Crop image"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="image-crop-box">
        <div className="image-crop-stage">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="image-crop-controls">
          <input type="range" min={1} max={3} step={0.01} value={zoom}
            aria-label="Zoom" onChange={(e) => setZoom(Number(e.target.value))} />
          <div className="image-crop-actions">
            <button type="button" onClick={onCancel}>Cancel</button>
            <button type="button" className="primary" disabled={!pixels}
              onClick={() => pixels && onConfirm(pixels)}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}
